import type React from 'react';
import { ViewPort } from '@/common/context/ViewPort/ViewPort';
import { defHalfOverlayStyles, defMobCenterOverlayStyles } from '@/components/utils/DefaultStyles';
import type { ShipType } from '@/common/interfaces/order.interface';
import { getEstDeliveyTimeEST } from '@/utilities/ShippingUtils';

export const REGULAR_ORDER_HEADER_PRIMARY_TEXT = 'Place an Order';
export const REGULAR_ORDER_HEADER_SECONDARY_TEXT =
  "Hey! If you order today, you'll get it delivered by May 25. Need any help?";
export const GROUP_ORDER_HEADER_PRIMARY_TEXT = 'Launch a Store';
export const STORE_CHECKOUT_HEADER_PRIMARY_TEXT = 'Launch your store & share the link!';
export const REGULAR_ORDER_HEADER_SECONDARY_TEXT_MOBILE = 'Order today & get it by May 25.';
export const GROUP_ORDER_HEADER_SECONDARY_TEXT =
  "When you launch the store, you'll get a store link to share with your group members.";
export const NON_STD_PLACEMENT_CONFIRMATION_TEXT1 =
  "I've checked the details of the designs and confirm that they can be printed as is.";
export const NON_STD_PLACEMENT_CONFIRMATION_TEXT2 =
  'I give your art team permission to make minor changes to image quality, size, and/or placement to ensure the design prints well.';
export const UPS_VALIDATION_INVALID_TEXT =
  'Oh no! UPS says that the address is invalid. Can you try again or enter a different address?';
export const UPS_VALIDATION_SUGGEST_TEXT =
  'Oh no! UPS doesn’t recognize that address. Can you use one of these instead?';
export const UPS_VALIDATION_SUGGEST_PARTIAL_TEXT = 'Oh no! UPS doesn’t recognize that address.';
export const REGULAR_ORDER_SUMMARY_TEXT = 'Confirm & Place Order';
export const REGULAR_ORDER_SUMMARY_TEXT_MOBILE = 'Place Order';
export const DOWNLOAD_CSV_TEXT = 'Download CSV Template';
export const DOWNLOAD_CSV_TEXT_MOBILE = 'CSV Template';
export const CART_UPLOADED_CSV_FILENAME_MAX_LENGTH = 28;
export const REGULAR_ORDER_FOOTER_TEXT = 'Order Summary >';
export const REGULAR_ORDER_FOOTER_TEXT_MOBILE = 'Summary >';
export const PLACE_ORDER_DISABLED_TOOLTIP = 'Please fill all the form inputs to submit the order.';
export const MAKE_PAYMENT_DISABLED_TOOLTIP = 'Please confirm the amount before payment';
export const PLACE_ORDER_PRICE_ZERO_DISABLED_TOOLTIP =
  'There was an error getting the quote. To proceed, please remove the items priced at $0.';
export const PLACE_ORDER_REJECTED_DISABLED_TOOLTIP =
  'Item(s) in your order have an issue. Please review them.';

/** CustomizeOrderStep item details subtitle — marks the custom name/number upload as mandatory. */
export const CUSTOMIZE_ORDER_ITEM_REQUIRED_LABEL = 'Required*';

/** Checkout V4 order summary — line item under subtotal/shipping when guarantee delivery is on. */
export const CHECKOUT_GUARANTEE_DELIVERY_TOTALS_LABEL = 'Guarantee Delivery';

/** Checkout V4 shipping method section — inline label on the guarantee delivery row. */
export const CHECKOUT_ADD_GUARANTEE_DELIVERY_LABEL = 'Add Guarantee Delivery';

/** Checkout Section Completed event section names (Checkout V4). */
export const CHECKOUT_SECTION_COMPLETED_NAMES = {
  CONFIRM_DESIGN: 'Confirm Design',
  SHIPPING_DETAILS: 'Shipping Details',
  SHIPPING_METHOD: 'Shipping Method',
  PAYMENT_DETAILS: 'Payment Details',
  INFO_FOR_UNIVERSITY_APPROVAL: 'Info for University Approval',
} as const;

/** Delay before auto-completing a prefilled checkout section after the prior section. */
export const CHECKOUT_SECTION_COMPLETED_PREFILL_DELAY_MS = 2500;

/** Delay before auto-completing Shipping Method after Shipping Details. */
export const CHECKOUT_SECTION_COMPLETED_SHIPPING_METHOD_DELAY_MS = 2000;

/** Checkout V4 page heading shown above the checkout form and order summary. */
export const CHECKOUT_V4_PAGE_TITLE = 'Checkout';

/** Checkout V4 header back-link label returning the user to the cart. */
export const CHECKOUT_V4_BACK_TO_CART_TEXT = 'Back to Cart';

/** Pay Later hint shown next to the clock icon in cart summary (all breakpoints). */
export const CART_SUMMARY_PAY_LATER_DESC_TEXT = "Or select 'Pay Later' at checkout";

/**
 * Checkout V4 wallet "reconnect a different account" label — used both as the
 * Braintree Drop-in `chooseAnotherWayToPay` translation and the PayPal row's own
 * "Add a New Card" action, so the two stay in sync.
 */
export const CHECKOUT_V4_ADD_NEW_CARD_LABEL = 'Add a New Card';

export const LIST_OF_STATES = [
  {
    label: 'AK',
    value: 'AK',
  },
  {
    label: 'AL',
    value: 'AL',
  },
  {
    label: 'AR',
    value: 'AR',
  },
  {
    label: 'AZ',
    value: 'AZ',
  },
  {
    label: 'CA',
    value: 'CA',
  },
  {
    label: 'CO',
    value: 'CO',
  },
  {
    label: 'CT',
    value: 'CT',
  },
  {
    label: 'DC',
    value: 'DC',
  },
  {
    label: 'DE',
    value: 'DE',
  },
  {
    label: 'FL',
    value: 'FL',
  },
  {
    label: 'GA',
    value: 'GA',
  },
  {
    label: 'HI',
    value: 'HI',
  },
  {
    label: 'IA',
    value: 'IA',
  },
  {
    label: 'ID',
    value: 'ID',
  },
  {
    label: 'IL',
    value: 'IL',
  },
  {
    label: 'IN',
    value: 'IN',
  },
  {
    label: 'KS',
    value: 'KS',
  },
  {
    label: 'KY',
    value: 'KY',
  },
  {
    label: 'LA',
    value: 'LA',
  },
  {
    label: 'MA',
    value: 'MA',
  },
  {
    label: 'MD',
    value: 'MD',
  },
  {
    label: 'ME',
    value: 'ME',
  },
  {
    label: 'MI',
    value: 'MI',
  },
  {
    label: 'MN',
    value: 'MN',
  },
  {
    label: 'MO',
    value: 'MO',
  },
  {
    label: 'MS',
    value: 'MS',
  },
  {
    label: 'MT',
    value: 'MT',
  },
  {
    label: 'NC',
    value: 'NC',
  },
  {
    label: 'ND',
    value: 'ND',
  },
  {
    label: 'NE',
    value: 'NE',
  },
  {
    label: 'NH',
    value: 'NH',
  },
  {
    label: 'NJ',
    value: 'NJ',
  },
  {
    label: 'NM',
    value: 'NM',
  },
  {
    label: 'NV',
    value: 'NV',
  },
  {
    label: 'NY',
    value: 'NY',
  },
  {
    label: 'OH',
    value: 'OH',
  },
  {
    label: 'OK',
    value: 'OK',
  },
  {
    label: 'OR',
    value: 'OR',
  },
  {
    label: 'PA',
    value: 'PA',
  },
  {
    label: 'RI',
    value: 'RI',
  },
  {
    label: 'SC',
    value: 'SC',
  },
  {
    label: 'SD',
    value: 'SD',
  },
  {
    label: 'TN',
    value: 'TN',
  },
  {
    label: 'TX',
    value: 'TX',
  },
  {
    label: 'UT',
    value: 'UT',
  },
  {
    label: 'VA',
    value: 'VA',
  },
  {
    label: 'VT',
    value: 'VT',
  },
  {
    label: 'WA',
    value: 'WA',
  },
  {
    label: 'WI',
    value: 'WI',
  },
  {
    label: 'WV',
    value: 'WV',
  },
  {
    label: 'WY',
    value: 'WY',
  },
];

export interface IStateSelectOption {
  label: string;
  value: string;
  fullName: string;
}

export const STATE_SELECT_OPTIONS_WITH_FULL_NAMES: IStateSelectOption[] = [
  { label: 'AK', value: 'AK', fullName: 'Alaska' },
  { label: 'AL', value: 'AL', fullName: 'Alabama' },
  { label: 'AR', value: 'AR', fullName: 'Arkansas' },
  { label: 'AZ', value: 'AZ', fullName: 'Arizona' },
  { label: 'CA', value: 'CA', fullName: 'California' },
  { label: 'CO', value: 'CO', fullName: 'Colorado' },
  { label: 'CT', value: 'CT', fullName: 'Connecticut' },
  { label: 'DC', value: 'DC', fullName: 'District of Columbia' },
  { label: 'DE', value: 'DE', fullName: 'Delaware' },
  { label: 'FL', value: 'FL', fullName: 'Florida' },
  { label: 'GA', value: 'GA', fullName: 'Georgia' },
  { label: 'HI', value: 'HI', fullName: 'Hawaii' },
  { label: 'IA', value: 'IA', fullName: 'Iowa' },
  { label: 'ID', value: 'ID', fullName: 'Idaho' },
  { label: 'IL', value: 'IL', fullName: 'Illinois' },
  { label: 'IN', value: 'IN', fullName: 'Indiana' },
  { label: 'KS', value: 'KS', fullName: 'Kansas' },
  { label: 'KY', value: 'KY', fullName: 'Kentucky' },
  { label: 'LA', value: 'LA', fullName: 'Louisiana' },
  { label: 'MA', value: 'MA', fullName: 'Massachusetts' },
  { label: 'MD', value: 'MD', fullName: 'Maryland' },
  { label: 'ME', value: 'ME', fullName: 'Maine' },
  { label: 'MI', value: 'MI', fullName: 'Michigan' },
  { label: 'MN', value: 'MN', fullName: 'Minnesota' },
  { label: 'MO', value: 'MO', fullName: 'Missouri' },
  { label: 'MS', value: 'MS', fullName: 'Mississippi' },
  { label: 'MT', value: 'MT', fullName: 'Montana' },
  { label: 'NC', value: 'NC', fullName: 'North Carolina' },
  { label: 'ND', value: 'ND', fullName: 'North Dakota' },
  { label: 'NE', value: 'NE', fullName: 'Nebraska' },
  { label: 'NH', value: 'NH', fullName: 'New Hampshire' },
  { label: 'NJ', value: 'NJ', fullName: 'New Jersey' },
  { label: 'NM', value: 'NM', fullName: 'New Mexico' },
  { label: 'NV', value: 'NV', fullName: 'Nevada' },
  { label: 'NY', value: 'NY', fullName: 'New York' },
  { label: 'OH', value: 'OH', fullName: 'Ohio' },
  { label: 'OK', value: 'OK', fullName: 'Oklahoma' },
  { label: 'OR', value: 'OR', fullName: 'Oregon' },
  { label: 'PA', value: 'PA', fullName: 'Pennsylvania' },
  { label: 'RI', value: 'RI', fullName: 'Rhode Island' },
  { label: 'SC', value: 'SC', fullName: 'South Carolina' },
  { label: 'SD', value: 'SD', fullName: 'South Dakota' },
  { label: 'TN', value: 'TN', fullName: 'Tennessee' },
  { label: 'TX', value: 'TX', fullName: 'Texas' },
  { label: 'UT', value: 'UT', fullName: 'Utah' },
  { label: 'VA', value: 'VA', fullName: 'Virginia' },
  { label: 'VT', value: 'VT', fullName: 'Vermont' },
  { label: 'WA', value: 'WA', fullName: 'Washington' },
  { label: 'WI', value: 'WI', fullName: 'Wisconsin' },
  { label: 'WV', value: 'WV', fullName: 'West Virginia' },
  { label: 'WY', value: 'WY', fullName: 'Wyoming' },
];

export const ORDER_NAME_FIELD_REQUIRED_MSG = 'Give your order a name.';
export const SHIPPING_FIELD_ERROR_MSGS = {
  SHIPPINGNAME_REQUIRED: 'Who should we ship this to?',
  SHIPPINGNAME_MAX: 'The shipping name is too long (35 char. max).',
  ATTENTIONNAME_MAX: 'The attention name is too long (35 char. max).',
  STREET1_REQUIRED: 'What’s your street # or name?',
  STREET1_MAX: 'The street # or name is too long (35 char. max).',
  STREET2_MAX: 'The apartment / suite # is too long (35 char. max).',
  CITY_REQUIRED: 'What’s your city?',
  CITY_MAX: 'The city name is too long (30 char. max).',
  STATE_REQUIRED: 'What’s your state?',
  ZIP_REQUIRED: 'What’s your ZIP code?',
  ZIP_INVALID: 'Enter a valid zip.',
};

export const EDIT_PARTICIPANT_ADDRESS_CHANGE_WARNING_MESSAGE =
  'Editing address may impact the total cost. Please review before saving.';

export const EDIT_PARTICIPANT_COLOR_CHANGE_WARNING_MESSAGE =
  'Editing color may impact the total cost. Please review before saving.';

export const EDIT_PARTICIPANT_STORE_NOT_LIVE_ERROR =
  'This store is no longer live. Your changes could not be saved.';

export const EDIT_PARTICIPANT_SEARCH_PLACEHOLDER = 'Search participants by name...';

export const EDIT_PARTICIPANT_FOCUS_SEARCH_QUERY_PARAM = 'focusSearch';

/** Deep-links the participants page to a single participant's detail view. */
export const PARTICIPANT_ID_QUERY_PARAM = 'participantId';

export const ORDER_STEP_CONFIG = {
  NAME: {
    title: 'Order Name',
    className: 'order-name',
  },
  SHIPPING: {
    title: 'Add Shipping Details',
    className: 'shipping-details',
  },
};

export const CREDIT_CARD_FIELDS = ['amount', 'name', 'creditCard', 'expiryDate', 'cvv', 'zipCode'];
export const SHIPPING_FIELDS = [
  'shippingName',
  'attentionName',
  'street1',
  'street2',
  'city',
  'state',
  'shippingZipCode',
];

export const REG_ORDER_STEPS_WITH_CUSTOMIZATION_COUNT = 6;
export const REG_ORDER_STEP_COUNT = 5;
export const STANDARD_SIZES = ['2XS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', 'OS'];

export const MOBILE_TAB_DESKTOP_SMALL_VIEWPORT = [
  ViewPort.mobile,
  ViewPort.tabSmall,
  ViewPort.tab,
  ViewPort.tabBig,
  ViewPort.desktopSmall,
];

export const MOBILE_SUMMARY_HALF_OVERLAY_STYLE: React.CSSProperties = {
  position: 'fixed',
  bottom: 0,
  top: 'auto',
  left: 0,
  right: 0,
  height: 'auto',
  maxHeight: '90vh',
  width: '100vw',
  margin: '0',
  padding: '0', // No padding - let inner components handle their own padding
  paddingTop: '0',
  paddingBottom: '0',
  borderRadius: '0',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  backgroundColor: '#FFFFFF',
  zIndex: 1001,
  animation: 'slideUpFromBottom 0.3s ease-out',
  overflowY: 'auto',
  overflowX: 'hidden',
};

export const TABLET_SUMMARY_OVERLAY_STYLE: React.CSSProperties = {
  position: 'fixed',
  bottom: 0,
  top: 'auto',
  left: 0,
  right: 0,
  height: '75vh',
  maxHeight: '75vh',
  width: '100vw',
  margin: '0',
  padding: '16px 30px 16px 30px',
  borderRadius: '0 0 16px 16px',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  backgroundColor: '#FFFFFF',
  zIndex: 1001,
  animation: 'slideUpFromBottom 0.3s ease-out',
  overflowY: 'auto',
};

export const FOOTER_SUMMARY_MODAL_STYLE = {
  ...defHalfOverlayStyles,
  bottom: 0,
  top: 'unset',
  height: 'auto',
  margin: '0',
  padding: '20px',
  borderColor: '#E2E6F2',
  zIndex: 1001,
};

export const MOBILE_SUMMARY_RIGHT_MODAL_STYLE = {
  ...defHalfOverlayStyles,
  bottom: 'unset',
  top: 'unset',
  left: 'unset',
  right: '4px',
  height: 'auto',
  width: 'auto',
  padding: '20px 30px 30px 30px',
  margin: '0',
  borderRadius: '20px',
};

export const MOBILE_SUMMARY_SHIP_TYPES_MODAL_STYLE = {
  ...defHalfOverlayStyles,
  bottom: 'unset',
  top: 'unset',
  left: 'unset',
  right: '0px',
  height: '-webkit-fill-available',
  width: 'auto',
  padding: '20px 30px 30px 30px',
  margin: '0',
  borderRadius: '20px',
  zIndex: 1001,
};

export const MOBILE_SUMMARY_RIGHT_OVERLAY_STYLE = {
  zIndex: 1000,
};

/** Bottom sheet style for cart summary when opened from fixed footer (mobile/tablet) */
export const CART_SUMMARY_BOTTOM_SHEET_STYLE: React.CSSProperties = {
  position: 'fixed',
  bottom: 0,
  top: 'auto',
  left: 0,
  right: 0,
  height: '90vh',
  maxHeight: '90vh',
  width: '100vw',
  margin: '0',
  padding: '0',
  paddingTop: '0',
  paddingBottom: '0',
  borderRadius: '20px 20px 0 0',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  backgroundColor: '#FFFFFF',
  zIndex: 10000,
  animation: 'slideUpFromBottom 0.3s ease-out',
  overflowY: 'auto',
  overflowX: 'hidden',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  border: '1px solid #E2E6F2',
  boxShadow: '0px -4px 20px rgba(133, 136, 172, 0.25)',
};

export const MOBILE_SHIP_TYPE_OVERLAY_STYLE = {
  zIndex: 1001,
};

/** Wrapper class for cart / order summary modal close control (see cart-summary-modal-close.scss). */
export const CART_SUMMARY_MODAL_CLOSE_WRAPPER_CLASS = 'cart-summary-modal-close';

export const MOBILE_SUMMARY_CLOSE_ICON_STYLE: React.CSSProperties = {
  top: 0,
  position: 'absolute',
  right: 0,
  padding: '20px 35px 0 0',
  cursor: 'pointer',
  zIndex: 1,
};

/** Left-edge close button — half inside / half outside the modal (store cart drawer, breakdown modal). */
export const STORE_MODAL_EDGE_CLOSE_ICON_WRAPPER_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 34,
  left: 0,
  right: 'auto',
  transform: 'translate(-50%, -50%)',
  padding: 0,
  cursor: 'pointer',
  zIndex: 10001,
};

/** @deprecated Use STORE_MODAL_EDGE_CLOSE_ICON_WRAPPER_STYLE */
export const STORE_CART_SUMMARY_MODAL_CLOSE_ICON_STYLE = STORE_MODAL_EDGE_CLOSE_ICON_WRAPPER_STYLE;

export const MOBILE_SHIPPING_STATE_OVERLAY_STYLE = {
  ...defHalfOverlayStyles,
  bottom: 0,
  top: 'unset',
  height: '65vh',
  margin: '0',
  padding: '60px 35px 0 35px',
};

export const MOBILE_UNI_APPROVAL_OVERLAY_STYLE = {
  ...defHalfOverlayStyles,
  bottom: 0,
  top: 'unset',
  height: '35vh',
  margin: '0',
  padding: '60px 35px 0 35px',
};

export const MOBILE_DESIGN_IMGS_OVERLAY_STYLE = {
  ...defHalfOverlayStyles,
  bottom: 0,
  top: 'unset',
  height: 'auto',
  margin: '0',
};

export const MOBILE_PROMO_APPLIED_OVERLAY = {
  ...defMobCenterOverlayStyles,
  height: '165px',
  width: '70%',
  borderRadius: '20px',
  padding: '20px',
  marginLeft: '10%',
  marginRight: '10%',
};

export const SHIPPING_METHODS: ShipType[] = [
  {
    title: 'Standard',
    estDelivery: getEstDeliveyTimeEST('Standard'),
    shipTypeKey: 'standard',
    deliveryTime: '14 Days',
    price: 'FREE Shipping',
    className: 'standard',
    hide: false,
    disable: false,
    tooltipMessg: '',
  },
  {
    title: 'Expedited',
    shipTypeKey: 'expedited',
    estDelivery: getEstDeliveyTimeEST('Expedited'),
    deliveryTime: '11 Days',
    price: 0,
    className: 'expedited',
    hide: false,
    disable: false,
    tooltipMessg: '',
  },
  {
    title: 'Fresh Prints Flash',
    shipTypeKey: 'flash',
    estDelivery: getEstDeliveyTimeEST('Flash'),
    deliveryTime: '5 Days',
    price: 0,
    className: 'flash',
    hide: false,
    disable: false,
    tooltipMessg: '',
  },
  {
    title: 'Made to order',
    shipTypeKey: 'mto',
    estDelivery: getEstDeliveyTimeEST('MTO'),
    deliveryTime: '4 Weeks',
    price: 'FREE Shipping',
    className: 'mto',
    hide: true,
    disable: false,
    tooltipMessg: '',
  },
  {
    title: 'Economy',
    estDelivery: '',
    shipTypeKey: 'economy',
    deliveryTime: '',
    price: 0,
    className: 'economy',
    hide: true,
    disable: false,
    tooltipMessg: '',
  },
];

export const MADE_TO_ORDER_SHIP_TEXT =
  'Our made-to-order garments are manufactured specially for you, which means they’re one of a kind but they also take longer than our standard garments.';
export const REACH_OUT_TEXT1 = 'Need it faster? We can make most timelines work.';
export const REACH_OUT_TEXT2 = 'Just reach out to us';
export const REACH_OUT_EMAIL = 'sales@freshprints.com';

export const STORE_NAME_FIELD_REQUIRED_MSG = 'Give your store a name.';
export const STORE_DEAD_LINE = 'Set your store deadline.';
export const STORE_DESCRIPTION_FIELD_REQUIRED_MSG = 'Give your store a description.';
export const PRODUCT_TITLE = 'Set your product title.';
export const PRODUCT_DESCRIPTION = 'Set your product description.';
export const STORE_PREVIEW_LOADER = 'Hold Tight, Preview Loading...';
export const STORE_LAUNCH = 'Hold Tight, Store Launching...';
export const DISABLED_STORE_TOOLTIP_LICENSING =
  "You can add this design to your store only after it is formally approved. Don't worry, we'll work with you to make it happen.";

export const DISABLED_CART_TOOLTIP_LICENSING =
  "You can add this design to your cart only after it is formally approved. Don't worry, we'll work with you to make it happen.";

export const INPROGRESS_STORE_TOOLTIP_LICENSING =
  "Our artists are working on your design. You can add to store and order once it's done.";

export const INPROGRESS_CART_TOOLTIP_LICENSING =
  "Our artists are working on your design. You can add to cart and order once it's done.";

export const ADDED_TO_STORE_TOOLTIP = 'Added to Store';
export const ADD_TO_STORE_BUTTON_LABEL = 'Add to Store';

export const DESIGN_CARD_ADD_TO_STORE_ICON_SRC = '/src/assets/svg/store_white.svg';
export const DESIGN_CARD_ADDED_TO_STORE_ICON_SRC = '/src/assets/svg/store_white_added.svg';
export const STORE_BUTTON_ADDED_TO_STORE_ICON_SRC = '/src/assets/svg/store_navy_added.svg';

export const PAYING_OPTIONS = [
  {
    label: 'Students',
    value: 'Students',
  },
  {
    label: 'University',
    value: 'University',
  },
];

export const PURPOSE_OPTIONS = [
  {
    label: 'Member apparel for Registered Student Organisation/Department/School',
    value: 'Member apparel for Registered Student Organisation/Department/School',
  },
  {
    label: 'Fundraising/Philanthropy Event',
    value: 'Fundraising/Philanthropy Event',
  },
  {
    label: 'Resale outside the university',
    value: 'Resale outside the university',
  },
];

export const UNIVERSITY_APPROVAL_TITLE = 'Info for University Approval';
export const UNIVERSITY_APPROVAL_TITLE_MOBILE = 'University Approval Info';
export const UNIVERSITY_APPROVAL_CLOSE_ARIA_LABEL = 'Close university approval dialog';
export const UNIVERSITY_APPROVAL_DESCRIPTION =
  'Your design has collegiate marks, so we need this info to submit it to the University for approval.';
export const UNIVERSITY_APPROVAL_CANCEL_LABEL = 'Cancel';
export const UNIVERSITY_APPROVAL_SUBMIT_LABEL = 'Submit';
export const UNIVERSITY_APPROVAL_SUBMITTING_LABEL = 'Submitting…';

export const ORDER_EDIT_PARTICIPANTS_ROUTE_SUFFIX = 'edit-participants';
export const ORDER_PERMANENT_STORE_PARTICIPANTS_ROUTE_SUFFIX = 'permanent-store-participants';

/** Order header / card status pill when a permanent store is still open. */
export const PERMANENT_STORE_STATUS_TAG_TEXT = 'Permanent Store';

/** Order header / card status pill when a permanent store has been closed. */
export const CLOSED_STORE_STATUS_TAG_TEXT = 'Closed store';

export const getOrderEditParticipantsPath = (
  orderId: string,
  options?: { focusSearch?: boolean }
): string => {
  const base = `/order/${orderId}/${ORDER_EDIT_PARTICIPANTS_ROUTE_SUFFIX}`;

  if (options?.focusSearch) {
    return `${base}?${EDIT_PARTICIPANT_FOCUS_SEARCH_QUERY_PARAM}=true`;
  }

  return base;
};

export const getOrderPermanentStoreParticipantsPath = (
  orderId: string,
  options?: { participantId?: string | number }
): string => {
  const base = `/order/${orderId}/${ORDER_PERMANENT_STORE_PARTICIPANTS_ROUTE_SUFFIX}`;

  if (options?.participantId || options?.participantId === 0) {
    return `${base}?${PARTICIPANT_ID_QUERY_PARAM}=${options.participantId}`;
  }

  return base;
};
